import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaClock,
  FaPaperPlane
} from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // EmailJS Configuration
    // Replace these with your actual EmailJS credentials from https://www.emailjs.com/
    const serviceId = 'service_v228npf'; // EmailJS Service ID
    const templateId = 'template_dzcm4pq'; // EmailJS Template ID
    const publicKey = 'dwmEsL4Wc1fwrBlS4'; // EmailJS Public Key

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      company: formData.company,
      message: formData.message,
      to_email: 'info@pasalebensmittel.com' // Your receiving email
    };

    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        // Hide success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      })
      .catch((error) => {
        console.error('FAILED...', error);
        setSubmitStatus('error');
        // Hide error message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="contact-page">
      {/* Header Section */}
      <section className="contact-header">
        <div className="contact-header-overlay"></div>
        <div className="container">
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info-section">
              <h2>{t('contact.info.title')}</h2>
              <div className="info-cards">
                <div className="info-card">
                  <div className="info-icon">
                    <FaPhone />
                  </div>
                  <div className="info-text">
                    <h3>{t('contact.info.phone')}</h3>
                    <p><a href="tel:+494032527151">+49 40 32527151</a></p>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon">
                    <FaEnvelope />
                  </div>
                  <div className="info-text">
                    <h3>{t('contact.info.email')}</h3>
                    <p><a href="mailto:info@pasalebensmittel.com">info@pasalebensmittel.com</a></p>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="info-text">
                    <h3>{t('contact.info.address')}</h3>
                    <p>Banksstraße 28</p>
                    <p>Grossmarkt Stand 252 Ost F</p>
                    <p>20097 Hamburg, Deutschland</p>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon">
                    <FaClock />
                  </div>
                  <div className="info-text">
                    <h3>{t('contact.info.hours')}</h3>
                    <p>{t('contact.hours.weekdays')}</p>
                    <p>{t('contact.hours.saturday')}</p>
                    <p>{t('contact.hours.sunday')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-card">
                <h2>{t('hero.contact')}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>{t('contact.form.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form.name')}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('contact.form.email')}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.form.email')}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('contact.form.phone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact.form.phone')}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t('contact.form.company')}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t('contact.form.company')}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('contact.form.message')}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder={t('contact.form.message')}
                    ></textarea>
                  </div>

                  {/* Success/Error Messages */}
                  {submitStatus === 'success' && (
                    <div className="alert alert-success">
                      ✅ {t('contact.form.successMessage') || 'Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.'}
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="alert alert-error">
                      ❌ {t('contact.form.errorMessage') || 'Mesaj gönderilemedi. Lütfen tekrar deneyin veya doğrudan info@pasalebensmittel.com adresine email gönderin.'}
                    </div>
                  )}

                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    <FaPaperPlane />
                    <span>{isSubmitting ? (t('contact.form.sending') || 'Gönderiliyor...') : t('contact.form.send')}</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="map-container">
          <iframe
            title="Pasa Lebensmittel Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2369.1234567890!2d10.0125!3d53.54!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b18f1e7eb1e6e1%3A0x1234567890abcdef!2sBanksstra%C3%9Fe%2028%2C%2020097%20Hamburg%2C%20Germany!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
          <div className="map-overlay-info">
            <div className="map-info-card">
              <FaMapMarkerAlt className="map-icon" />
              <div className="map-text">
                <h3>Pasa Lebensmittel GmbH</h3>
                <p>Banksstraße 28, Grossmarkt Stand 252 Ost F</p>
                <p>20097 Hamburg, Deutschland</p>
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=Banksstraße+28,+20097+Hamburg,+Deutschland"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="directions-btn"
                >
                  <FaMapMarkerAlt /> {t('contact.getDirections')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

